import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { trackMemberAreaClick } from "@/lib/analytics";
import { memberAreaUrl } from "@/lib/memberUrl";
import { useLandingData } from "@/context/LandingDataContext";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { content } = useLandingData();
  const heroData = content?.hero || {
    overline: "DA'VM Collection",
    title: "Kemewahan Sempurna",
    italicTitle: "untuk Kulit Anda.",
    subtitle: "Rangkaian perawatan eksklusif DA'VM — mencerahkan, meremajakan, dan merawat kulit Anda dengan bahan pilihan premium yang terinspirasi ritual kecantikan mewah.",
    bgImage: "https://images.unsplash.com/photo-1618434958571-459c9c972ae8?q=85&w=2000&auto=format&fit=crop",
    stats: [
      { num: "7+", label: "Produk Eksklusif" },
      { num: "100%", label: "Bahan Terpilih" },
      { num: "BPOM", label: "Terdaftar Resmi" },
      { num: "1000+", label: "Wanita Percaya" }
    ]
  };

  return (
    <section
      id="beranda"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#F5F2EB]"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroData.bgImage})` }}
      />
      {/* Warm overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/70 via-[#FDFBF7]/40 to-[#FDFBF7]/85" />
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-48 pb-24 md:pb-32">
        <motion.p
          initial="hidden"
          animate="show"
          custom={0}
          variants={fadeUp}
          data-testid="hero-overline"
          className="text-xs tracking-[0.4em] uppercase text-[#BCA38F] mb-8"
        >
          — {heroData.overline}
        </motion.p>

        <motion.h1
          initial="hidden"
          animate="show"
          custom={1}
          variants={fadeUp}
          data-testid="hero-title"
          className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[1.02] tracking-tight text-[#1A1A1A] font-light max-w-4xl"
        >
          {heroData.title}
          <br />
          <em className="italic font-normal text-[#8A6E52]">{heroData.italicTitle}</em>
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="show"
          custom={2}
          variants={fadeUp}
          data-testid="hero-subtitle"
          className="mt-10 max-w-xl text-base md:text-lg text-[#5C5C5C] leading-relaxed"
        >
          {heroData.subtitle}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="show"
          custom={3}
          variants={fadeUp}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href="#produk"
            data-testid="hero-explore-button"
            className="group inline-flex items-center gap-3 rounded-full bg-[#1A1A1A] text-[#FDFBF7] px-8 h-14 text-xs tracking-[0.22em] uppercase hover:bg-[#BCA38F] transition-all duration-500"
          >
            Eksplorasi Produk
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
          </a>
          <a
            href={memberAreaUrl()}
            onClick={() => trackMemberAreaClick("hero")}
            data-testid="hero-member-link"
            className="group inline-flex items-center gap-2 rounded-full border border-[#1A1A1A]/20 text-[#1A1A1A] px-8 h-14 text-xs tracking-[0.22em] uppercase hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-all duration-500"
          >
            Kunjungi Member Area
            <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        {/* Highlight strip */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={4}
          variants={fadeUp}
          className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl border-t border-[#1A1A1A]/10 pt-10"
        >
          {heroData.stats?.map((stat, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] font-light">
                {stat.num}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#5C5C5C]">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Marquee bottom */}
      <div className="relative z-10 border-t border-black/5 bg-[#FDFBF7]/60 backdrop-blur-sm overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4 text-xs tracking-[0.35em] uppercase text-[#1A1A1A]/60">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 pr-10 shrink-0">
              {[
                "Brightening",
                "Whitening",
                "Glow",
                "Hydrating",
                "Nourishing",
                "Premium",
                "Regenerating",
                "Elegant",
              ].map((w, i) => (
                <span key={i} className="flex items-center gap-10">
                  <span>◆ {w}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

